import React, { useEffect, useMemo, useRef, useState } from 'react'
import { formatEditTypeLabel, type PrefillFieldEdit } from './submissionPrefillEditUtils'
import {
  SubmissionEditValueDisplay,
  TableRowComparePreview,
  buildFormFieldMetaById,
  getTableRowCompareContext
} from './submissionEditValueDisplay'
import ReactModal from 'react-modal'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'
import { REVIEW_PREFILL_SUBMISSION_EDITS } from '../../../graphql/mutation'
import { GET_USER_EMAIL_SIGNATURE } from '../../../graphql/query'
import './prefillEditsReviewModal.scss'

const SIGNATURE_REQUIRED_MESSAGE = 'Please add signature'

const modalStyles: ReactModal.Styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1060,
    backgroundColor: 'rgba(15, 23, 42, 0.45)'
  },
  content: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    width: '92vw',
    maxWidth: '880px',
    maxHeight: 'calc(100vh - 3rem)',
    padding: 0,
    border: '1px solid #eef0f4',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 16px 48px rgba(15, 23, 42, 0.12)',
    display: 'flex',
    flexDirection: 'column'
  }
}

interface Props {
  isOpen: boolean
  onClose: () => void
  saveFormUuid: string
  recipientName?: string | null
  recipientEmail?: string | null
  edits: PrefillFieldEdit[]
  formData?: unknown
  onReviewed: () => void
}

interface EditGroup {
  key: string
  title: string
  isConditional: boolean
  edits: PrefillFieldEdit[]
}

const PrefillEditsReviewModal: React.FC<Props> = ({
  isOpen,
  onClose,
  saveFormUuid,
  recipientName,
  recipientEmail,
  edits,
  formData,
  onReviewed
}) => {
  const fieldMetaById = useMemo(() => buildFormFieldMetaById(formData), [formData])
  const selectAllRef = useRef<HTMLInputElement>(null)
  const [reviewEdits, { loading }] = useMutation(REVIEW_PREFILL_SUBMISSION_EDITS, {
    context: { headers: { 'access-control-allow-methods': 'Form-Builder' } }
  })

  const { refetch: refetchSignature } = useQuery(GET_USER_EMAIL_SIGNATURE, {
    skip: !isOpen,
    context: { headers: { 'access-control-allow-methods': 'Form-Builder' } },
    fetchPolicy: 'network-only'
  })

  const pendingEdits = edits.filter((edit) => edit.status === 'pending')
  const pendingFieldIds = pendingEdits.map((edit) => edit.fieldId)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [remark, setRemark] = useState('')

  useEffect(() => {
    if (isOpen) {
      setSelectedIds(pendingFieldIds)
      setRemark('')
    }
  }, [isOpen, pendingFieldIds.join(',')])

  const allSelected =
    pendingFieldIds.length > 0 && pendingFieldIds.every((id) => selectedIds.includes(id))
  const someSelected = pendingFieldIds.some((id) => selectedIds.includes(id))

  useEffect(() => {
    if (!selectAllRef.current) return
    selectAllRef.current.indeterminate = someSelected && !allSelected
  }, [someSelected, allSelected])

  const toggleField = (fieldId: number) => {
    setSelectedIds((prev) =>
      prev.includes(fieldId) ? prev.filter((id) => id !== fieldId) : [...prev, fieldId]
    )
  }

  const selectAll = () => setSelectedIds(pendingFieldIds)
  const deselectAll = () => setSelectedIds([])
  const toggleSelectAll = () => {
    if (allSelected) {
      deselectAll()
      return
    }
    selectAll()
  }

  const handleAction = async (action: 'approve' | 'reject') => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one field to review')
      return
    }

    const signatureResult = await refetchSignature()
    if (!signatureResult.data?.getUserEmailSignature?.hasSignature) {
      toast.error(SIGNATURE_REQUIRED_MESSAGE)
      return
    }

    try {
      const trimmedRemark = remark.trim()
      const { data } = await reviewEdits({
        variables: {
          input: {
            saveFormUuid,
            fieldIds: selectedIds,
            action,
            ...(trimmedRemark ? { remark: trimmedRemark } : {})
          }
        }
      })
      toast.success(data?.reviewPrefillSubmissionEdits?.message || 'Review saved')
      onReviewed()
      onClose()
    } catch (err: any) {
      toast.error(err.message || 'Failed to save review')
    }
  }

  const renderBlankValue = () => <span className='text-muted'>n/a</span>

  const getConditionalFieldLabel = (edit: PrefillFieldEdit, partIndex: number): string =>
    edit.editFieldLabel || edit.label.split(' — ').pop() || `Field ${partIndex + 1}`

  const getGroupTitle = (edit: PrefillFieldEdit): string => {
    if (edit.scope === 'table_row') {
      const parent = edit.parentLabel || 'Table'
      const row = edit.rowHeader || (edit.rowIndex !== undefined ? `Row ${edit.rowIndex + 1}` : '')
      return row ? `${parent} — ${row}` : parent
    }
    if (edit.editType === 'conditional') {
      return edit.parentLabel || edit.label
    }
    return edit.label
  }

  /** Non-conditional field/table-row edit that carries the primary previous + main change. */
  const getPrimaryMainEdit = (group: EditGroup): PrefillFieldEdit | undefined =>
    group.edits.find((edit) => edit.editType !== 'conditional')

  const getRowMainEdit = (group: EditGroup): PrefillFieldEdit | undefined =>
    group.edits.find((edit) => edit.scope === 'table_row' && edit.editType !== 'conditional')

  const getConditionalEdits = (group: EditGroup): PrefillFieldEdit[] =>
    group.edits.filter((edit) => edit.editType === 'conditional')

  const getPrefillValueForGroup = (group: EditGroup): string => {
    // Prefer the main / prefilled field previous value so it stays stable when
    // conditional file uploads are grouped into the same card.
    const mainEdit = getPrimaryMainEdit(group)
    if (mainEdit) return String(mainEdit.originalValue ?? '').trim()

    const conditionalEdit = getConditionalEdits(group)[0]
    if (!conditionalEdit) return ''

    return String(conditionalEdit.parentOriginalValue ?? conditionalEdit.originalValue ?? '').trim()
  }

  const renderPrefillForGroup = (group: EditGroup) => {
    const mainEdit = getPrimaryMainEdit(group)
    const first = mainEdit || group.edits[0]
    const prefillValue = getPrefillValueForGroup(group)
    if (!first || !prefillValue) return renderBlankValue()

    // Only table-row edits use the table compare path — never non-table conditionals.
    const isTableRowContext = first.scope === 'table_row' && first.editType !== 'conditional'
    const tableFieldId =
      first.tableFieldId || (first.scope === 'table_row' ? first.parentFieldId : undefined)

    if (tableFieldId && isTableRowContext) {
      return (
        <SubmissionEditValueDisplay
          value={prefillValue}
          fieldId={tableFieldId}
          fieldMetaById={fieldMetaById}
          tableFieldId={tableFieldId}
          isTableRowEdit
          muted
        />
      )
    }

    return (
      <SubmissionEditValueDisplay
        value={prefillValue}
        fieldId={first.parentFieldId || first.fieldId}
        fieldMetaById={fieldMetaById}
        muted
      />
    )
  }

  const renderChangedValue = (edit: PrefillFieldEdit) => {
    const changedValue = String(edit.submittedValue ?? '').trim()
    if (!changedValue) return renderBlankValue()

    const fieldMeta = fieldMetaById.get(edit.fieldId)
    const editFieldType = fieldMeta?.type

    return (
      <SubmissionEditValueDisplay
        value={changedValue}
        fieldId={edit.fieldId}
        fieldMetaById={fieldMetaById}
        tableFieldId={edit.tableFieldId}
        editFieldType={editFieldType}
        isTableRowEdit={edit.scope === 'table_row' && edit.editType !== 'conditional'}
      />
    )
  }

  const getGroupTypeLabel = (group: EditGroup): string => {
    const mainEdit = getPrimaryMainEdit(group)
    const hasConditional = getConditionalEdits(group).length > 0
    if (mainEdit?.scope === 'table_row' && hasConditional) return 'Table row'
    if (mainEdit && hasConditional) return 'Prefilled field'
    if (mainEdit?.scope === 'table_row') return 'Table row'
    return formatEditTypeLabel(group.edits[0])
  }

  const groupedEdits = useMemo((): EditGroup[] => {
    const groups: EditGroup[] = []
    const groupIndexByKey = new Map<string, number>()

    edits.forEach((edit) => {
      const isConditional = edit.editType === 'conditional'
      // Group main field + its conditionals together (e.g. Address + File upload 1).
      const groupKey =
        edit.scope === 'table_row' && edit.tableFieldId !== undefined
          ? `table-row-${edit.tableFieldId}-${edit.rowIndex ?? 0}`
          : isConditional && edit.parentFieldId
          ? `field-${edit.parentFieldId}`
          : `field-${edit.fieldId}`

      const existingIndex = groupIndexByKey.get(groupKey)
      if (existingIndex === undefined) {
        groupIndexByKey.set(groupKey, groups.length)
        groups.push({
          key: groupKey,
          title: getGroupTitle(edit),
          isConditional,
          edits: [edit]
        })
        return
      }

      groups[existingIndex].edits.push(edit)
      if (isConditional) {
        groups[existingIndex].isConditional = true
      }
      // Prefer the main-field title (Address) over a conditional title.
      if (!isConditional) {
        groups[existingIndex].title = getGroupTitle(edit)
      }
    })

    return groups
  }, [edits])

  const renderGroupStatus = (group: EditGroup) => {
    const statuses = group.edits.map((edit) => edit.status)
    const status = statuses.every((value) => value === 'approved')
      ? 'approved'
      : statuses.every((value) => value === 'rejected')
      ? 'rejected'
      : statuses.some((value) => value === 'pending')
      ? 'pending'
      : statuses[0]

    return (
      <span
        className={`prefill-edits-review-modal__status prefill-edits-review-modal__status--${status}`}
      >
        {status}
      </span>
    )
  }

  const getGroupRemark = (group: EditGroup): string => {
    const withRemark = group.edits.find((edit) => String(edit.remark || '').trim())
    return String(withRemark?.remark || '').trim()
  }

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      ariaHideApp={false}
      style={modalStyles}
      shouldCloseOnOverlayClick
      parentSelector={() => document.body}
    >
      <div className='prefill-edits-review-modal__content'>
        <div className='prefill-edits-review-modal__header'>
          <div>
            <h5 className='prefill-edits-review-modal__title'>Review edits</h5>
            {(recipientName || recipientEmail) && (
              <p className='prefill-edits-review-modal__subtitle'>
                {recipientName || 'Recipient'}
                {recipientEmail ? ` · ${recipientEmail}` : ''}
              </p>
            )}
          </div>
          <button type='button' className='btn-close' onClick={onClose} aria-label='Close' />
        </div>

        {pendingEdits.length > 0 ? (
          <div className='prefill-edits-review-modal__toolbar'>
            <div className='prefill-edits-review-modal__selection'>
              <label className='prefill-edits-review-modal__selection-label'>
                <input
                  ref={selectAllRef}
                  type='checkbox'
                  className='form-check-input'
                  checked={allSelected}
                  onChange={toggleSelectAll}
                />
                All pending
              </label>
              <div className='prefill-edits-review-modal__selection-actions'>
                <button
                  type='button'
                  className='prefill-edits-review-modal__link-btn'
                  onClick={selectAll}
                  disabled={allSelected}
                >
                  Select all
                </button>
                <span className='prefill-edits-review-modal__divider'>|</span>
                <button
                  type='button'
                  className='prefill-edits-review-modal__link-btn'
                  onClick={deselectAll}
                  disabled={selectedIds.length === 0}
                >
                  Deselect all
                </button>
              </div>
            </div>
            <span className='prefill-edits-review-modal__count'>
              {selectedIds.length} of {pendingEdits.length} selected
            </span>
          </div>
        ) : null}

        <div className='prefill-edits-review-modal__body'>
          <div className='prefill-edits-review-modal__list'>
            {groupedEdits.map((group) => {
              const pendingInGroup = group.edits.filter((edit) => edit.status === 'pending')
              const selectedInGroup = pendingInGroup.filter((edit) =>
                selectedIds.includes(edit.fieldId)
              )
              const isGroupSelected =
                pendingInGroup.length > 0 && selectedInGroup.length === pendingInGroup.length
              const primaryMainEdit = getPrimaryMainEdit(group)
              const mainRowEdit = getRowMainEdit(group)
              const conditionalEdits = getConditionalEdits(group)
              const tableCompare =
                mainRowEdit && !conditionalEdits.length
                  ? getTableRowCompareContext(mainRowEdit, fieldMetaById)
                  : null
              const showChangePartLabels = Boolean(primaryMainEdit) && conditionalEdits.length > 0
              const groupRemark = getGroupRemark(group)

              return (
                <div
                  key={group.key}
                  className={`prefill-edits-review-modal__card${
                    isGroupSelected ? ' prefill-edits-review-modal__card--selected' : ''
                  }`}
                >
                  <div className='prefill-edits-review-modal__card-head'>
                    {pendingInGroup.length > 0 ? (
                      <input
                        type='checkbox'
                        className='form-check-input mt-1'
                        checked={isGroupSelected}
                        onChange={() => {
                          const ids = pendingInGroup.map((edit) => edit.fieldId)
                          if (isGroupSelected) {
                            setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)))
                            return
                          }
                          setSelectedIds((prev) => Array.from(new Set(prev.concat(ids))))
                        }}
                        aria-label={`Select ${group.title}`}
                      />
                    ) : (
                      <span style={{ width: 16 }} aria-hidden='true' />
                    )}
                    <div className='prefill-edits-review-modal__card-titles'>
                      <p className='prefill-edits-review-modal__item-title'>{group.title}</p>
                    </div>
                    <div className='prefill-edits-review-modal__item-meta'>
                      <span className='prefill-edits-review-modal__type'>
                        {getGroupTypeLabel(group)}
                      </span>
                      {renderGroupStatus(group)}
                    </div>
                  </div>

                  {tableCompare && mainRowEdit ? (
                    <TableRowComparePreview
                      originalValue={mainRowEdit.originalValue}
                      submittedValue={mainRowEdit.submittedValue}
                      tableFieldId={tableCompare.tableFieldId}
                      rowIndex={tableCompare.rowIndex}
                      fieldMetaById={fieldMetaById}
                    />
                  ) : (
                    <div className='prefill-edits-review-modal__compare'>
                      <div className='prefill-edits-review-modal__value-block'>
                        <span className='prefill-edits-review-modal__value-label'>Previous</span>
                        <div className='prefill-edits-review-modal__value-content prefill-edits-review-modal__value-content--muted'>
                          {renderPrefillForGroup(group)}
                        </div>
                      </div>

                      <div className='prefill-edits-review-modal__value-block'>
                        <span className='prefill-edits-review-modal__value-label'>Changes</span>
                        <div className='prefill-edits-review-modal__changed-list'>
                          {primaryMainEdit ? (
                            <div className='prefill-edits-review-modal__changed-item'>
                              {showChangePartLabels ? (
                                <div className='prefill-edits-review-modal__changed-item-head'>
                                  <span className='prefill-edits-review-modal__changed-item-label'>
                                    {primaryMainEdit.scope === 'table_row'
                                      ? primaryMainEdit.rowHeader || 'Row values'
                                      : primaryMainEdit.label || 'Value'}
                                  </span>
                                </div>
                              ) : null}
                              <div className='prefill-edits-review-modal__value-content'>
                                {renderChangedValue(primaryMainEdit)}
                              </div>
                            </div>
                          ) : null}

                          {conditionalEdits.map((edit, partIndex) => {
                            const isPending = edit.status === 'pending'
                            const isSelected = selectedIds.includes(edit.fieldId)
                            const showPartLabel =
                              conditionalEdits.length > 1 || showChangePartLabels

                            return (
                              <div
                                key={`${edit.fieldId}-${
                                  edit.editFieldLabel || edit.label
                                }-${partIndex}`}
                                className='prefill-edits-review-modal__changed-item'
                              >
                                {showPartLabel ? (
                                  <div className='prefill-edits-review-modal__changed-item-head'>
                                    {isPending ? (
                                      <input
                                        type='checkbox'
                                        className='form-check-input'
                                        checked={isSelected}
                                        onChange={() => toggleField(edit.fieldId)}
                                        aria-label={`Select ${getConditionalFieldLabel(
                                          edit,
                                          partIndex
                                        )}`}
                                      />
                                    ) : null}
                                    <span className='prefill-edits-review-modal__changed-item-label'>
                                      {getConditionalFieldLabel(edit, partIndex)}
                                    </span>
                                  </div>
                                ) : null}
                                <div className='prefill-edits-review-modal__value-content'>
                                  {renderChangedValue(edit)}
                                </div>
                              </div>
                            )
                          })}

                          {!primaryMainEdit && conditionalEdits.length === 0
                            ? group.edits.map((edit) => (
                                <div
                                  key={edit.fieldId}
                                  className='prefill-edits-review-modal__changed-item'
                                >
                                  <div className='prefill-edits-review-modal__value-content'>
                                    {renderChangedValue(edit)}
                                  </div>
                                </div>
                              ))
                            : null}
                        </div>
                      </div>
                    </div>
                  )}

                  {groupRemark ? (
                    <div className='prefill-edits-review-modal__remark'>
                      <span className='prefill-edits-review-modal__remark-label'>Remarks</span>
                      <p className='prefill-edits-review-modal__remark-text'>{groupRemark}</p>
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        </div>

        <div className='prefill-edits-review-modal__footer'>
          {pendingEdits.length > 0 ? (
            <div className='prefill-edits-review-modal__remark-input'>
              <label
                className='prefill-edits-review-modal__remark-input-label'
                htmlFor='prefill-edit-review-remark'
              >
                Remarks <span className='text-muted'>(optional)</span>
              </label>
              <textarea
                id='prefill-edit-review-remark'
                className='form-control form-control-sm'
                rows={2}
                maxLength={1000}
                placeholder='Add a note for approval or rejection…'
                value={remark}
                onChange={(event) => setRemark(event.target.value)}
                disabled={loading}
              />
            </div>
          ) : null}
          <div className='prefill-edits-review-modal__footer-actions'>
            <button
              type='button'
              className='btn btn-sm btn-light'
              onClick={onClose}
              disabled={loading}
            >
              {pendingEdits.length > 0 ? 'Cancel' : 'Close'}
            </button>
            {pendingEdits.length > 0 ? (
              <>
                <button
                  type='button'
                  className='btn btn-sm btn-light-danger'
                  onClick={() => handleAction('reject')}
                  disabled={loading || selectedIds.length === 0}
                >
                  Reject
                </button>
                <button
                  type='button'
                  className='btn btn-sm btn-primary'
                  onClick={() => handleAction('approve')}
                  disabled={loading || selectedIds.length === 0}
                >
                  Approve
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </ReactModal>
  )
}

export type { PrefillFieldEdit } from './submissionPrefillEditUtils'
export default PrefillEditsReviewModal
